// "use client";

// import { zero_pad_number } from "@core/helpers/string";
// import { useEffect, useState } from "react";

// const useCountDown = ({ number, countdown_className } = {}) => {
// 	const [seconds, set_seconds] = useState(number);

// 	const convert_countdown_time = (time, formats = ["minutes", "seconds"]) => {
// 		const month_format = zero_pad_number(parseInt((time / 60 / 60 / 24 / 30) % 12));
// 		const days_format = zero_pad_number(parseInt((time / 60 / 60 / 24) % 30));
// 		const hours_format = zero_pad_number(parseInt((time / 60 / 60) % 24));
// 		const minutes_format = zero_pad_number(parseInt((time / 60) % 60));
// 		const seconds_format = zero_pad_number(time % 60);

// 		return (
// 			<div className={`${countdown_className || ""}`}>
// 				{formats?.includes("months") && <span className="timer days">{month_format}</span>}
// 				{formats?.includes("months") ? <span className="time-divider">:</span> : ''}

// 				{formats?.includes("days") && <span className="timer days">{days_format}</span>}
// 				{formats?.includes("days") ? <span className="time-divider">:</span> : ''}

// 				{formats?.includes("hours") && <span className="timer hours"> {hours_format}</span>}
// 				{formats?.includes("hours") ? <span className="time-divider">:</span> : ''}

// 				{formats?.includes("minutes") && <span className="timer minutes">{minutes_format}</span>}
// 				{formats?.includes("minutes") ? <span className="time-divider">:</span> : ''}

// 				{formats?.includes("seconds") && <span className="timer seconds">{seconds_format}</span>}
// 			</div>
// 		);
// 	};

// 	useEffect(() => {
// 		const interval = setInterval(() => {
// 			if (!seconds || seconds <= 0) {
// 				clearInterval(interval);
// 			} else {
// 				set_seconds((prevSeconds) => prevSeconds - 1);
// 			}
// 		}, 1000);

// 		return () => clearInterval(interval);
// 	}, [seconds]);

// 	return {
// 		counter_down: seconds,
// 		set_counter_down: set_seconds,
// 		convert_countdown_time,
// 	};
// };

// export default useCountDown;


"use client";

import { zero_pad_number } from "@core/helpers/string";
import { useEffect, useState } from "react";
import { useBfcacheRestore } from "@core/helpers/client/bfcache";

/* Core 14's timer, for a call written for core 14: `convert_countdown_time(time, ["days", "hours", ...])`, the formats
   as an array (core 15 takes `{ formats }` and ignored the array: such timers showed only mm:ss). Same markup as core
   14: the parts are direct children of the box (`[&>.timer]` / `[&>.time-divider]` selectors), days within the month
   (`% 30`), hours with core 14's leading space. Used by memory-market and isource. The digits come from the time, which
   is a few seconds later in the browser than on the server: `suppressHydrationWarning`, the first tick shows the
   browser's. */
const core14_countdown = (time, formats, countdown_className) => {
	const month_format = zero_pad_number(parseInt((time / 60 / 60 / 24 / 30) % 12));
	const days_format = zero_pad_number(parseInt((time / 60 / 60 / 24) % 30));
	const hours_format = zero_pad_number(parseInt((time / 60 / 60) % 24));
	const minutes_format = zero_pad_number(parseInt((time / 60) % 60));
	const seconds_format = zero_pad_number(time % 60);

	return (
		<div className={`${countdown_className || ""}`}>
			{formats?.includes("months") && <span className="timer days" suppressHydrationWarning>{month_format}</span>}
			{formats?.includes("months") ? <span className="time-divider">:</span> : ''}

			{formats?.includes("days") && <span className="timer days" suppressHydrationWarning>{days_format}</span>}
			{formats?.includes("days") ? <span className="time-divider">:</span> : ''}

			{formats?.includes("hours") && <span className="timer hours" suppressHydrationWarning> {hours_format}</span>}
			{formats?.includes("hours") ? <span className="time-divider">:</span> : ''}

			{formats?.includes("minutes") && <span className="timer minutes" suppressHydrationWarning>{minutes_format}</span>}
			{formats?.includes("minutes") ? <span className="time-divider">:</span> : ''}

			{formats?.includes("seconds") && <span className="timer seconds" suppressHydrationWarning>{seconds_format}</span>}
		</div>
	);
};

// `core14_markup`: every convert_countdown_time call of this timer is drawn as on core 14 (core14_countdown), also a
// call without formats (minutes and seconds). A call with an array is drawn so without the option too.
const useCountDown = ({ number = 0, countdown_className, core14_markup = false } = {}) => {
	const [seconds, set_seconds] = useState(number);

	useEffect(() => {
		set_seconds(number || 0);
	}, [number]);

	// bfcache (all sites, asked by jument): timers do not tick while the page sits in the back/forward cache;
	// on restore the time it was frozen is taken off, so an offer or OTP timer shows the real time left
	useBfcacheRestore((event, { frozen_ms } = {}) => {
		const frozen_seconds = Math.floor((frozen_ms || 0) / 1000);
		if (frozen_seconds > 0) {
			set_seconds((prev) => Math.max(0, prev - frozen_seconds));
		}
	});

	const convert_countdown_time = (time, options = {}) => {
		// core 14's call: the formats as an array
		if (Array.isArray(options)) return core14_countdown(time, options, countdown_className);
		if (core14_markup) return core14_countdown(time, options?.formats || ["minutes", "seconds"], countdown_className);
		const { formats = ["minutes", "seconds"], convertDaysToHours = false } = options || {};
		const totalSeconds = Math.max(0, parseInt(time || 0));

		const month_format = zero_pad_number(parseInt((totalSeconds / 60 / 60 / 24 / 30) % 12));
		const rawDays = parseInt(totalSeconds / 60 / 60 / 24);
		const rawHours = parseInt((totalSeconds / 60 / 60) % 24);
		const minutes_format = zero_pad_number(parseInt((totalSeconds / 60) % 60));
		const seconds_format = zero_pad_number(parseInt(totalSeconds % 60));

		const finalHours = convertDaysToHours
			? zero_pad_number((rawDays * 24) + rawHours)
			: zero_pad_number(rawHours);

		const days_format = zero_pad_number(rawDays);

		const visibleFormats = [...formats];

		if (convertDaysToHours) {
			const dayIndex = visibleFormats.indexOf("days");
			if (dayIndex !== -1) {
				visibleFormats.splice(dayIndex, 1);
			}
		}

		const values = {
			months: month_format,
			days: days_format,
			hours: finalHours,
			minutes: minutes_format,
			seconds: seconds_format,
		};

		return (
			<div className={`${countdown_className || ""}`}>
				{visibleFormats.map((format, index) => (
					<span key={format}>
						<span className={`timer ${format}`}>{values[format]}</span>
						{index < visibleFormats.length - 1 && (
							<span className="time-divider">:</span>
						)}
					</span>
				))}
			</div>
		);
	};

	useEffect(() => {
		if (seconds <= 0) return;

		const interval = setInterval(() => {
			set_seconds((prevSeconds) => {
				if (prevSeconds <= 1) {
					clearInterval(interval);
					return 0;
				}
				return prevSeconds - 1;
			});
		}, 1000);

		return () => clearInterval(interval);
	}, [seconds]);

	return {
		counter_down: seconds,
		set_counter_down: set_seconds,
		convert_countdown_time,
	};
};

export default useCountDown;

